import { CloudBody, CloudView, RegionBody, RegionView } from '@lemoncloud/codes-goods-api';
import { Params } from '@lemoncloud/lemon-web-core';
import { AxiosRequestConfig, GenericAbortSignal } from 'axios';

import { ListResult } from '@codes/shared';
import { webCore } from '@codes/web-core';

import { UpdateCloudDTO, UpdateRegionDTO } from '../types';

const BACKEND_ENDPOINT = import.meta.env.VITE_BACKEND_ENDPOINT.toLowerCase();

export const fetchClouds = async (params: Params): Promise<ListResult<CloudView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/clouds/0/list`,
        })
        .setParams({ ...params })
        .execute<ListResult<CloudView>>();

    return { ...data };
};

export const searchClouds = async (params: Params): Promise<ListResult<CloudView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/clouds/0/list`,
        })
        .setParams({ ...params })
        .execute<ListResult<CloudView>>();

    return { ...data };
};

export const fetchCloudById = async (cloudId?: string, signal?: GenericAbortSignal): Promise<CloudView> => {
    if (!cloudId) {
        throw new Error('fetchCloudById: @cloudId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/clouds/${cloudId}`,
        })
        .addAxiosRequestConfig({
            ...(signal && { signal }),
        } as AxiosRequestConfig)
        .execute<CloudView>();

    return { ...data };
};

export const createCloud = async (cloudBody: CloudBody): Promise<CloudView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BACKEND_ENDPOINT}/clouds/0`,
        })
        .setBody({ ...cloudBody })
        .execute<CloudView>();

    return data;
};

export const updateCloud = async (updateCloudDTO: UpdateCloudDTO): Promise<CloudView> => {
    const { cloudId, ...data } = updateCloudDTO;
    if (!cloudId) {
        throw new Error('updateCloud: @cloudId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/clouds/${cloudId}`,
        })
        .setBody(data)
        .execute<CloudView>();

    return response.data;
};

export const updateCloudAtAdmin = async (updateCloudDTO: UpdateCloudDTO): Promise<CloudView> => {
    const { cloudId, ...data } = updateCloudDTO;
    if (!cloudId) {
        throw new Error('updateCloud: @cloudId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/clouds/${cloudId}/admin`,
        })
        .setBody(data)
        .execute<CloudView>();

    return response.data;
};

export const deleteCloud = async (cloudId: string): Promise<CloudView> => {
    if (!cloudId) {
        throw new Error('deleteCloud: @cloudId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${BACKEND_ENDPOINT}/clouds/${cloudId}`,
        })
        .setBody({})
        .execute<CloudView>();

    return data;
};

export const fetchRegions = async (params: Params): Promise<ListResult<RegionView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/regions/0/list`,
        })
        .setParams({ limit: 50, ...params })
        .execute<ListResult<RegionView>>();

    return { ...data };
};

export const fetchRegionById = async (regionId?: string, signal?: GenericAbortSignal): Promise<RegionView> => {
    if (!regionId) {
        throw new Error('fetchRegionById: @regionId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/regions/${regionId}/detail`,
        })
        .addAxiosRequestConfig({
            ...(signal && { signal }),
        } as AxiosRequestConfig)
        .execute<RegionView>();

    return { ...data };
};

export const createRegion = async (regionBody: RegionBody): Promise<RegionView> => {
    if (!regionBody.cloudId) {
        throw new Error('createRegion: .cloudId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BACKEND_ENDPOINT}/regions/0`,
        })
        .setBody({ ...regionBody })
        .execute<RegionView>();

    return response.data;
};

export const updateRegion = async (updateRegionDTO: UpdateRegionDTO): Promise<RegionView> => {
    const { regionId, ...data } = updateRegionDTO;
    if (!regionId) {
        throw new Error('updateRegion: @regionId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/regions/${regionId}`,
        })
        .setBody({ ...data })
        .execute<RegionView>();

    return response.data;
};

export const updateRegionAtAdmin = async (updateRegionDTO: UpdateRegionDTO): Promise<RegionView> => {
    const { regionId, ...data } = updateRegionDTO;
    if (!regionId) {
        throw new Error('updateRegion: @regionId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/regions/${regionId}/admin`,
        })
        .setBody({ ...data })
        .execute<RegionView>();

    return response.data;
};

export const deleteRegion = async (regionId: string): Promise<RegionView> => {
    if (!regionId) {
        throw new Error('deleteRegion: @regionId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${BACKEND_ENDPOINT}/regions/${regionId}`,
        })
        .setBody({})
        .execute<RegionView>();

    return data;
};
